import type {
  ApiKeySummary,
  AuthContext,
  AuthenticatedUser,
  CreatedApiKey,
  CreatedInvitation,
  InvitationPreview,
  InvitationSummary,
  PasswordResetRequestResult,
  RecoveryMode,
  RecoveryModeResponse,
  ResetLinkResult,
  ResetPasswordResult,
  UserAccount,
  UserPreferences,
} from '@ofmos/shared/auth';
import type { CreateRoleRequest, Role, RoleHolder, UpdateRoleRequest } from '@ofmos/shared/roles';
import type { WorkspaceInfoResponse } from '@ofmos/shared/workspace';
import type { AuthClient } from '../client';
import type { Transport } from './transport';

/**
 * `AuthClient` over `/api/auth/*`.
 *
 * `me()` is the only call in the app that treats a 401 as a RESULT rather than an error:
 * "nobody is signed in" is the normal state of a login screen, and a guard that had to
 * catch an exception to learn it would turn every cold load into a red console.
 * Everything else propagates, so an expired session mid-session reaches the transport's
 * `onUnauthenticated` hook and the app redirects once.
 */
export class HttpAuthClient implements AuthClient {
  constructor(private readonly transport: Transport) {}

  async me(): Promise<AuthenticatedUser | null> {
    const response = await this.transport.requestMaybe<AuthContext>({
      method: 'GET',
      path: '/api/auth/me',
      allowUnauthenticated: true,
    });
    // An API key is authenticated but is not a person; the UI has no session for it.
    return response?.user ?? null;
  }

  async login(email: string, password: string): Promise<AuthenticatedUser> {
    const response = await this.transport.request<{ user: AuthenticatedUser }>({
      method: 'POST',
      path: '/api/auth/login',
      body: { email, password },
    });
    return response.user;
  }

  async logout(): Promise<void> {
    await this.transport.request<null>({ method: 'POST', path: '/api/auth/logout' });
  }

  async register(input: {
    readonly email: string;
    readonly password: string;
    readonly name: string;
  }): Promise<AuthenticatedUser> {
    const response = await this.transport.request<{ user: AuthenticatedUser }>({
      method: 'POST',
      path: '/api/auth/register',
      body: input,
    });
    return response.user;
  }

  async listUsers(): Promise<readonly UserAccount[]> {
    const response = await this.transport.request<{ users: readonly UserAccount[] }>({
      method: 'GET',
      path: '/api/auth/users',
    });
    return response.users;
  }

  async createUser(input: {
    readonly email: string;
    readonly password: string;
    readonly name: string;
    readonly roleIds: readonly string[];
  }): Promise<AuthenticatedUser> {
    const response = await this.transport.request<{ user: AuthenticatedUser }>({
      method: 'POST',
      path: '/api/auth/users',
      body: input,
    });
    return response.user;
  }

  async updateUser(
    userId: string,
    changes: {
      readonly isActive?: boolean;
      readonly name?: string;
    },
  ): Promise<UserAccount> {
    const response = await this.transport.request<{ user: UserAccount }>({
      method: 'PATCH',
      path: `/api/auth/users/${userId}`,
      body: changes,
    });
    return response.user;
  }

  /** The password is in this response and nowhere else, ever. Never logged, never stored. */
  resetPassword(userId: string): Promise<ResetPasswordResult> {
    return this.transport.request<ResetPasswordResult>({
      method: 'POST',
      path: `/api/auth/users/${userId}/reset-password`,
    });
  }

  changeOwnPassword(input: {
    readonly currentPassword: string;
    readonly newPassword: string;
  }): Promise<{ readonly revokedSessions: number }> {
    return this.transport.request<{ revokedSessions: number }>({
      method: 'POST',
      path: '/api/auth/me/password',
      body: input,
    });
  }

  async getPreferences(): Promise<UserPreferences> {
    const response = await this.transport.request<{ preferences: UserPreferences }>({
      method: 'GET',
      path: '/api/auth/me/preferences',
    });
    return response.preferences;
  }

  async setPreference(key: string, value: unknown): Promise<void> {
    await this.transport.request<{ key: string; value: unknown }>({
      method: 'PUT',
      // The key is a dotted lower-case identifier the server re-validates; nothing a
      // user types reaches this path.
      path: `/api/auth/me/preferences/${key}`,
      body: { value },
    });
  }

  async listApiKeys(): Promise<readonly ApiKeySummary[]> {
    const response = await this.transport.request<{ apiKeys: readonly ApiKeySummary[] }>({
      method: 'GET',
      path: '/api/auth/api-keys',
    });
    return response.apiKeys;
  }

  async createApiKey(input: {
    readonly label: string;
    readonly roleIds: readonly string[];
    readonly isAgent: boolean;
  }): Promise<CreatedApiKey> {
    const response = await this.transport.request<{ apiKey: CreatedApiKey }>({
      method: 'POST',
      path: '/api/auth/api-keys',
      body: input,
    });
    return response.apiKey;
  }

  async revokeApiKey(keyId: string): Promise<boolean> {
    const response = await this.transport.request<{ revoked: boolean }>({
      method: 'DELETE',
      path: `/api/auth/api-keys/${keyId}`,
    });
    return response.revoked;
  }

  // --- one-time links (accounts F1) ----------------------------------------

  async listInvitations(): Promise<readonly InvitationSummary[]> {
    const response = await this.transport.request<{ invitations: readonly InvitationSummary[] }>({
      method: 'GET',
      path: '/api/auth/invitations',
    });
    return response.invitations;
  }

  /** The accept path is in this response and nowhere else, ever. Never logged, never stored. */
  createInvitation(input: {
    readonly email: string;
    readonly roleIds: readonly string[];
  }): Promise<CreatedInvitation> {
    return this.transport.request<CreatedInvitation>({
      method: 'POST',
      path: '/api/auth/invitations',
      body: input,
    });
  }

  async revokeInvitation(invitationId: string): Promise<void> {
    await this.transport.request<undefined>({
      method: 'DELETE',
      path: `/api/auth/invitations/${invitationId}`,
    });
  }

  /**
   * PUBLIC, and the token is in the PATH because that is where the person's link put it.
   * It is url-encoded on the way out even though the server's own alphabet is base64url:
   * this value came off a URL bar somebody pasted into.
   */
  getInvitation(token: string): Promise<InvitationPreview> {
    return this.transport.request<InvitationPreview>({
      method: 'GET',
      path: `/api/auth/invitations/${encodeURIComponent(token)}`,
    });
  }

  async acceptInvitation(
    token: string,
    input: { readonly name: string; readonly password: string },
  ): Promise<AuthenticatedUser> {
    const response = await this.transport.request<{ user: AuthenticatedUser }>({
      method: 'POST',
      path: `/api/auth/invitations/${encodeURIComponent(token)}/accept`,
      body: input,
    });
    return response.user;
  }

  createResetLink(userId: string): Promise<ResetLinkResult> {
    return this.transport.request<ResetLinkResult>({
      method: 'POST',
      path: `/api/auth/users/${userId}/reset-link`,
    });
  }

  async getRecoveryMode(): Promise<RecoveryMode> {
    const response = await this.transport.request<RecoveryModeResponse>({
      method: 'GET',
      path: '/api/auth/recovery',
    });
    return response.mode;
  }

  /**
   * `GET /api/workspace` - the sign-in screen's second line (login.md 2.3). Its own OPEN
   * route rather than a field on the admin-only `/api/instance`, so a signed-out visitor
   * learns the installation's name and nothing else about it.
   */
  getWorkspace(): Promise<WorkspaceInfoResponse> {
    return this.transport.request<WorkspaceInfoResponse>({
      method: 'GET',
      path: '/api/workspace',
    });
  }

  requestPasswordReset(email: string): Promise<PasswordResetRequestResult> {
    return this.transport.request<PasswordResetRequestResult>({
      method: 'POST',
      path: '/api/auth/password-reset/request',
      body: { email },
    });
  }

  async checkPasswordReset(token: string): Promise<void> {
    await this.transport.request<{ status: 'valid' }>({
      method: 'GET',
      path: `/api/auth/password-reset/${encodeURIComponent(token)}`,
    });
  }

  async setPasswordWithToken(token: string, password: string): Promise<void> {
    await this.transport.request<{ revokedSessions: number }>({
      method: 'POST',
      path: `/api/auth/password-reset/${encodeURIComponent(token)}`,
      body: { password },
    });
  }

  // --- access roles (roles-and-permissions.md section 6) --------------------

  async listRoles(): Promise<readonly Role[]> {
    const response = await this.transport.request<{ roles: readonly Role[] }>({
      method: 'GET',
      path: '/api/roles',
    });
    return response.roles;
  }

  async getRole(roleId: string): Promise<Role> {
    const response = await this.transport.request<{ role: Role }>({
      method: 'GET',
      path: `/api/roles/${roleId}`,
    });
    return response.role;
  }

  async listRoleHolders(roleId: string): Promise<readonly RoleHolder[]> {
    const response = await this.transport.request<{ holders: readonly RoleHolder[] }>({
      method: 'GET',
      path: `/api/roles/${roleId}/holders`,
    });
    return response.holders;
  }

  async listRoleAdministrators(): Promise<readonly RoleHolder[]> {
    const response = await this.transport.request<{ administrators: readonly RoleHolder[] }>({
      method: 'GET',
      path: '/api/roles/administrators',
    });
    return response.administrators;
  }

  async createRole(input: CreateRoleRequest): Promise<Role> {
    const response = await this.transport.request<{ role: Role }>({
      method: 'POST',
      path: '/api/roles',
      body: input,
    });
    return response.role;
  }

  async updateRole(roleId: string, patch: UpdateRoleRequest): Promise<Role> {
    const response = await this.transport.request<{ role: Role }>({
      method: 'PATCH',
      path: `/api/roles/${roleId}`,
      body: patch,
    });
    return response.role;
  }

  async deleteRole(roleId: string): Promise<void> {
    await this.transport.request<undefined>({
      method: 'DELETE',
      path: `/api/roles/${roleId}`,
    });
  }

  async assignUserRoles(userId: string, roleIds: readonly string[]): Promise<void> {
    await this.transport.request<{ roleIds: readonly string[] }>({
      method: 'PUT',
      path: `/api/auth/users/${userId}/roles`,
      body: { roleIds },
    });
  }

  async assignApiKeyRoles(apiKeyId: string, roleIds: readonly string[]): Promise<void> {
    await this.transport.request<{ roleIds: readonly string[] }>({
      method: 'PUT',
      path: `/api/auth/api-keys/${apiKeyId}/roles`,
      body: { roleIds },
    });
  }
}
